import { z } from 'zod';

import {
  ROUNDING_MODE_OPTIONS,
} from '#/utils/roundingMode';

const lineSchema = z.object({
  id: z.string().optional().nullable(),
  productId: z.string().min(1, '請選擇產品'),
  salesUnitName: z.string().optional().nullable(),
  orderQty: z.union([z.string(), z.number(), z.null()]),
  cancelQty: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
  unitPrice: z.union([z.string(), z.number(), z.null()]),
  discountRate: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
  discountAmount: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
  remark: z.string().optional().nullable(),
}).superRefine((line, ctx) => {
  const qty = Number(line.orderQty);
  if (line.orderQty === '' || line.orderQty == null || Number.isNaN(qty) || qty <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '訂購數量須大於 0',
      path: ['orderQty'],
    });
  }

  const cancel = line.cancelQty === '' || line.cancelQty == null
    ? 0
    : Number(line.cancelQty);
  if (Number.isNaN(cancel) || cancel < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '取消量不可為負',
      path: ['cancelQty'],
    });
  } else if (!Number.isNaN(qty) && cancel > qty) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '取消量不可大於訂購量',
      path: ['cancelQty'],
    });
  }

  const price = Number(line.unitPrice);
  if (line.unitPrice === '' || line.unitPrice == null || Number.isNaN(price) || price < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '請填寫有效單價',
      path: ['unitPrice'],
    });
  }

  if (line.discountRate !== '' && line.discountRate != null) {
    const rate = Number(line.discountRate);
    if (Number.isNaN(rate) || rate < 0 || rate > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '折數須為 0～1（例如 0.9＝九折）',
        path: ['discountRate'],
      });
    }
  }

  // 每件固定折扣與折數二擇一；不可為負或超過單價
  if (line.discountAmount !== '' && line.discountAmount != null) {
    const perUnit = Number(line.discountAmount);
    if (line.discountRate !== '' && line.discountRate != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '折數與每件折扣只能擇一填寫',
        path: ['discountAmount'],
      });
    } else if (Number.isNaN(perUnit) || perUnit < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '每件折扣不可為負',
        path: ['discountAmount'],
      });
    } else if (!Number.isNaN(price) && perUnit > price) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '每件折扣不可大於單價',
        path: ['discountAmount'],
      });
    }
  }
});

/**
 * header `tax_type`（寫入權威；勿再依賴 `price_tax_mode`）。
 * UI：應稅／含稅／免稅；ZERO_RATED 為 legacy-compatible，勿自動改寫。
 */
export const CUSTOMER_ORDER_TAX_TYPE_OPTIONS = [
  { value: 'TAXABLE', label: '應稅' },
  { value: 'TAX_INCLUDED', label: '含稅' },
  { value: 'TAX_EXEMPT', label: '免稅' },
  { value: 'ZERO_RATED', label: '零稅率' },
];

/**
 * @deprecated 改用 `#/utils/roundingMode` 的 `ROUNDING_MODE_OPTIONS`
 */
export const CUSTOMER_ORDER_ROUNDING_MODE_OPTIONS = ROUNDING_MODE_OPTIONS;

/** 稅率必須為 0 的課稅別 */
export const CUSTOMER_ORDER_ZERO_TAX_RATE_TYPES = new Set([
  'TAX_EXEMPT',
  'ZERO_RATED',
]);

/** @deprecated 改用 CUSTOMER_ORDER_TAX_TYPE_OPTIONS；寫入勿送 price_tax_mode */
export const CUSTOMER_ORDER_PRICE_TAX_MODE_OPTIONS = [
  { value: 'TAX_EXCLUSIVE', label: '未稅單價' },
  { value: 'TAX_INCLUSIVE', label: '含稅單價' },
];

/** @deprecated 改用 CUSTOMER_ORDER_TAX_TYPE_OPTIONS（稅在 header） */
export const CUSTOMER_ORDER_LINE_TAX_TYPE_OPTIONS = CUSTOMER_ORDER_TAX_TYPE_OPTIONS;

export function formatCustomerOrderTaxTypeLabel(taxType) {
  const match = CUSTOMER_ORDER_TAX_TYPE_OPTIONS.find(
    (option) => option.value === taxType,
  );
  return match?.label ?? (taxType || '—');
}

/**
 * CO `tax_type` → SO `tax_mode`（建立銷貨單預填）。
 * TAX_INCLUDED→TAX_INCLUSIVE；TAX_EXEMPT→TAX_EXEMPT；其餘→TAX_EXCLUSIVE。
 */
export function mapCustomerOrderTaxTypeToSalesOrderTaxMode(taxType) {
  if (taxType === 'TAX_INCLUDED') {
    return 'TAX_INCLUSIVE';
  }
  if (taxType === 'TAX_EXEMPT') {
    return 'TAX_EXEMPT';
  }
  return 'TAX_EXCLUSIVE';
}

function refineDuplicateLineProducts(lines, ctx) {
  const seen = new Set();
  lines.forEach((line, index) => {
    const id = line.productId?.trim();
    if (!id) {
      return;
    }
    if (seen.has(id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '同一產品不可重複',
        path: [index, 'productId'],
      });
      return;
    }
    seen.add(id);
  });
}

const manualLineSchema = z.object({
  id: z.string().optional().nullable(),
  itemName: z.string().trim().min(1, '請填寫品名'),
  orderQty: z.union([z.string(), z.number(), z.null()]),
  unitName: z.string().trim().min(1, '請填寫單位'),
  unitPrice: z.union([z.string(), z.number(), z.null()]),
  remark: z.string().optional().nullable(),
  cancelQty: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
}).superRefine((line, ctx) => {
  const qty = Number(line.orderQty);
  if (line.orderQty === '' || line.orderQty == null || Number.isNaN(qty) || qty <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '訂購數量須大於 0',
      path: ['orderQty'],
    });
  }

  const cancel = line.cancelQty === '' || line.cancelQty == null
    ? 0
    : Number(line.cancelQty);
  if (Number.isNaN(cancel) || cancel < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '取消量不可為負',
      path: ['cancelQty'],
    });
  } else if (!Number.isNaN(qty) && cancel > qty) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '取消量不可大於訂購量',
      path: ['cancelQty'],
    });
  }

  const price = Number(line.unitPrice);
  if (line.unitPrice === '' || line.unitPrice == null || Number.isNaN(price) || price < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '請填寫有效單價',
      path: ['unitPrice'],
    });
  }
});

export const customerOrderFormSchema = z.object({
  customerOrderDate: z.string().min(1, '請填寫訂單日'),
  customerId: z.string().min(1, '請選擇客戶'),
  billingAddressId: z.string().min(1, '請選擇帳單地址'),
  shippingAddressId: z.string().min(1, '請選擇收貨地址'),
  shippingContactId: z.string().optional().nullable(),
  currencyId: z.string().min(1, '請選擇幣別'),
  taxType: z.enum(
    ['TAXABLE', 'TAX_INCLUDED', 'ZERO_RATED', 'TAX_EXEMPT'],
    { message: '請選擇課稅別' },
  ),
  taxRate: z.union([z.string(), z.number(), z.null()]),
  /** write 必選 UP／DOWN／HALF_UP；空／非法 →「請選擇進位方式」（禁 ''→HALF_UP）。 */
  roundingMode: z.enum(['UP', 'DOWN', 'HALF_UP'], {
    message: '請選擇進位方式',
  }),
  issueReason: z.string().min(1, '請填寫訂單事由'),
  remark: z.string().optional().nullable(),
  lines: z.array(lineSchema).default([]).superRefine(refineDuplicateLineProducts),
  manualLines: z.array(manualLineSchema).default([]),
}).superRefine((values, ctx) => {
  const productCount = (values.lines ?? []).length;
  const manualCount = (values.manualLines ?? []).length;
  if (productCount + manualCount < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '請至少新增一筆產品明細或其他產品',
      path: ['lines'],
    });
  }

  const rate = Number(values.taxRate);
  const isEmpty = values.taxRate === '' || values.taxRate == null;
  const isInvalidNumber = isEmpty || Number.isNaN(rate);

  if (CUSTOMER_ORDER_ZERO_TAX_RATE_TYPES.has(values.taxType)) {
    if (isInvalidNumber || rate !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '免稅／零稅率時稅率須為 0',
        path: ['taxRate'],
      });
    }
    return;
  }

  if (values.taxType === 'TAX_INCLUDED') {
    if (isInvalidNumber || rate <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '含稅時稅率須大於 0',
        path: ['taxRate'],
      });
    }
    return;
  }

  if (values.taxType === 'TAXABLE') {
    if (isInvalidNumber || rate < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '應稅時請填寫有效稅率（不可為負；可為 0）',
        path: ['taxRate'],
      });
    }
  }
});

export const CUSTOMER_ORDER_FIELD_LABELS = {
  customerOrderDate: '訂單日',
  customerId: '客戶',
  billingAddressId: '帳單地址',
  shippingAddressId: '收貨地址',
  shippingContactId: '收貨聯絡人',
  currencyId: '幣別',
  taxType: '課稅別',
  taxRate: '稅率',
  roundingMode: '進位方式',
  issueReason: '訂單事由',
  remark: '備註',
  lines: '明細',
  'lines.productId': '產品',
  'lines.salesUnitName': '銷售單位',
  'lines.orderQty': '訂購數量',
  'lines.cancelQty': '取消量',
  'lines.unitPrice': '單價',
  'lines.discountRate': '折數',
  'lines.discountAmount': '每件折扣',
  'lines.remark': '行備註',
  manualLines: '其他產品',
  'manualLines.itemName': '品名',
  'manualLines.unitName': '單位',
  'manualLines.orderQty': '訂購數量',
  'manualLines.cancelQty': '取消量',
  'manualLines.unitPrice': '單價',
  'manualLines.remark': '行備註',
};

const LINE_FIELD_LABELS = {
  productId: '產品',
  salesUnitName: '銷售單位',
  orderQty: '訂購數量',
  cancelQty: '取消量',
  unitPrice: '單價',
  discountRate: '折數',
  discountAmount: '每件折扣',
  remark: '行備註',
};

const MANUAL_LINE_FIELD_LABELS = {
  itemName: '品名',
  unitName: '單位',
  orderQty: '訂購數量',
  cancelQty: '取消量',
  unitPrice: '單價',
  remark: '行備註',
};

export function resolveCustomerOrderFieldLabel(path) {
  const lineMatch = path.match(/^lines\.(\d+)\.(.+)$/);
  if (lineMatch) {
    const index = Number(lineMatch[1]) + 1;
    const field = lineMatch[2];
    return `明細 #${index} ${LINE_FIELD_LABELS[field] || field}`;
  }

  const manualMatch = path.match(/^manualLines\.(\d+)\.(.+)$/);
  if (manualMatch) {
    const index = Number(manualMatch[1]) + 1;
    const field = manualMatch[2];
    return `其他產品 #${index} ${MANUAL_LINE_FIELD_LABELS[field] || field}`;
  }

  return undefined;
}

export const CUSTOMER_ORDER_LINES_TIP = (
  '請先選擇客戶，再選該客戶可用的產品；同一張訂單不可重複同一產品。'
  + ' 物料組成請到產品主檔維護。'
);

export const CUSTOMER_ORDER_MANUAL_LINES_TIP = (
  '其他產品不用選產品主檔，也不會進入工單規劃或庫存配置。'
  + ' 產品明細與其他產品合計至少一筆。'
);

/** @deprecated 改用 CUSTOMER_ORDER_MANUAL_LINES_TIP */
export const CUSTOMER_ORDER_CUSTOM_LINES_TIP = CUSTOMER_ORDER_MANUAL_LINES_TIP;

export const CUSTOMER_ORDER_SCHEDULE_GROUPS_TIP = (
  '依預交日分組；同一產品跨日排程量加總不可超過訂購數量，可少於訂購量（不扣取消量）。'
);

export const CUSTOMER_ORDER_TAX_TYPE_TIP = (
  '應稅：合計後再外加稅金。含稅：填寫金額已含稅。'
  + ' 免稅與零稅率不加稅。'
);
